import React from 'react';
import { Box, Button, Chip, Collapse, Typography } from '@mui/material';
import { Bot } from '../lib/types';
import { parseJd } from '../lib/botPage';
import { fmtIct } from '../lib/ict';
import { ProvLine } from '../chips';
import { Markdown } from '../Markdown';
import { RADIUS } from '../lib/brand';

const KIND_LABEL: Record<string, string> = { mandate: 'Mandate', scope: 'Scope', kpis: 'KPIs', cadence: 'Cadence', decision_rights: 'Decision rights', hard_rules: 'Hard rules' };

/** The job description as one collapsible section per `##` heading. Classified sections start open; the rest start closed. */
export function JdView({ bot }: { bot: Bot }) {
  const jd = React.useMemo(() => parseJd(bot.jd_markdown, bot.jd_sections || []), [bot.jd_markdown, bot.jd_sections]);
  const [open, setOpen] = React.useState<Record<string, boolean>>({});
  const isOpen = (h: string, def: boolean) => (h in open ? open[h] : def);
  const setAll = (v: boolean) => setOpen(Object.fromEntries(jd.sections.map((s) => [s.heading, v])));

  if (!bot.jd_markdown) {
    return <Typography variant="body2" color="text.secondary">No job description file found{bot.jd_markdown_path ? ` at ${bot.jd_markdown_path}` : ''}.</Typography>;
  }

  const download = () => {
    const url = URL.createObjectURL(new Blob([bot.jd_markdown || ''], { type: 'text/markdown;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${bot.slug}-job-description.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.2 }}>
        <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 200 }}>{jd.title || `${bot.name} job description`}</Typography>
        <Button size="small" variant="outlined" onClick={() => setAll(true)}>Expand all</Button>
        <Button size="small" variant="outlined" onClick={() => setAll(false)}>Collapse all</Button>
        <Button size="small" variant="contained" onClick={download}>Download JD (.md)</Button>
      </Box>
      {jd.intro && <Box sx={{ mb: 1.2 }}><Markdown>{jd.intro}</Markdown></Box>}
      {jd.sections.map((s) => {
        const o = isOpen(s.heading, s.defaultOpen);
        return (
          <Box key={s.heading} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: RADIUS.lg, mb: 1, overflow: 'hidden' }}>
            <Box
              component="button"
              type="button"
              aria-expanded={o}
              onClick={() => setOpen((cur) => ({ ...cur, [s.heading]: !o }))}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', textAlign: 'left', p: 1.2, border: 0, bgcolor: o ? 'action.hover' : 'transparent', cursor: 'pointer', color: 'text.primary', fontSize: 14, fontWeight: 700 }}
            >
              <span aria-hidden>{o ? '▾' : '▸'}</span>
              <span style={{ flex: 1 }}>{s.heading}</span>
              {KIND_LABEL[s.kind] && <Chip size="small" label={KIND_LABEL[s.kind]} sx={{ height: 20, fontSize: 10.5 }} />}
            </Box>
            <Collapse in={o} unmountOnExit>
              <Box sx={{ p: 1.4 }}>{s.body ? <Markdown>{s.body}</Markdown> : <Typography variant="caption" color="text.secondary">Empty section.</Typography>}</Box>
            </Collapse>
          </Box>
        );
      })}
      <ProvLine prov={bot.jd_prov} fmt={fmtIct} />
    </Box>
  );
}
